import React, { FunctionComponent } from 'react'
import { useT } from '@transifex/react'
import { Link } from '@lightspeed/design-system-react'
import { InputErrorMessage } from './InputErrorMessage'

interface ContinueAnywayMessageProps {
  inventoryLevel: number
  onContinue: () => void
  trackingLabel: string
}

/**
 * Reusable message component for handling insufficient inventory scenarios.
 *
 * Displays an error stating how many items are available and renders a
 * "Continue anyway?" link that allows the user to proceed with the sale
 * despite low or insufficient stock.
 *
 * @param props.inventoryLevel The quantity of inventory currently available for the item.
 * @param props.onContinue Callback invoked when the user chooses to continue despite the warning.
 * @param props.trackingLabel Analytics tracking label applied to the "Continue anyway?" link.
 */
export const ContinueAnywayMessage: FunctionComponent<ContinueAnywayMessageProps> = ({
  inventoryLevel,
  onContinue,
  trackingLabel,
}) => {
  const t = useT()

  return (
    <InputErrorMessage
      errorElement={
        <>
          {t('You only have {inventoryLevel} available. ', {
            _context: 'Not enough inventory error',
            inventoryLevel,
          })}
          <Link
            href="#"
            onClick={onContinue}
            data-cy="ignore-low-stock-warning"
            isSecondary={true}
            data-track={trackingLabel}
          >
            {t('Continue anyway?', {
              _context:
                'Link to proceed with sale despite insufficient inventory',
            })}
          </Link>
        </>
      }
    />
  )
}
